import { useState, useEffect, useCallback, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'

import { TableCell } from '@/components/ui/table'
import {
  useUpdateTable,
  useCreateTable,
  useDeleteTable,
  TableColumn,
  TableRow,
  Table,
  TemplateAttribute,
} from '@/services/hooks/template_attributes'
import type { FlattenedDocumentPage } from '@/lib/api/client'
import { EditableTable, createNewColumn } from './EditableTable'
import { useDocumentState } from '@/state/document'
import { useFullToolsBOM } from './OperationDocument/hooks/useBOM'

export type ToolsTableData = {
  toolName: string
  [accessor: string]: any
}

type ToolsTableProps = {
  template?: TemplateAttribute<Table>
  documentPage: FlattenedDocumentPage
  isReadOnly?: boolean
  dataType: 'tools-table'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const ToolsTable = ({
  template,
  documentPage,
  isReadOnly = false,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: ToolsTableProps) => {
  const draggableId = useMemo(
    () => template?.id || dataType,
    [template?.id, dataType],
  )
  const initialColumns = useMemo<Record<string, ColumnDef<ToolsTableData>>>(
    () => ({
      toolName: {
        accessorKey: 'toolName',
        header: 'Tools',
        size: 200,
        cell: ({ getValue }) => (
          <TableCell className="flex flex-1 items-center border-x border-solid border-gray-200 border-collapse text-xs py-1 min-h-6">
            <span className="px-1">{getValue<string>()}</span>
          </TableCell>
        ),
      },
    }),
    [],
  )

  const initialTableData = useMemo<Array<ToolsTableData>>(
    () => [
      {
        toolName: '',
      },
    ],
    [],
  )

  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null)
  const [tableData, setTableData] = useState<ToolsTableData[]>(() => [])
  const [columnsDef, setColumnsDef] = useState<ColumnDef<ToolsTableData>[]>([])
  const [columnsData, setColumnsData] = useState({})

  const toolsColumns = useMemo(
    () => template?.template_values.columns,
    [template],
  )
  const toolsRows = useMemo(() => template?.template_values.rows, [template])
  const tablePosition = useMemo(
    () => template?.template_values.position,
    [template?.template_values.position],
  )
  const columnsSize = useMemo(
    () => template?.template_values.columnsSize,
    [template?.template_values.columnsSize],
  )
  const isManual = useMemo(
    () => template?.template_values.isManual,
    [template?.template_values.isManual],
  )

  const toolsBom = useFullToolsBOM()

  const { mutateAsync: updateTable } = useUpdateTable()
  const { mutateAsync: createTable } = useCreateTable()
  const { mutateAsync: deleteTable } = useDeleteTable()

  const saveTableData = useCallback(
    async ({
      columns,
      rows,
    }: {
      columns?: Array<TableColumn>
      rows?: Array<TableRow>
    }) => {
      if (template?.id) {
        const tableData: Table = {
          type: dataType,
        }

        if (columns) {
          tableData.columns = columns
        }

        if (rows) {
          tableData.rows = rows
        }

        await updateTable({
          templateAttributeId: template?.id,
          documentPageId: documentPage.id as string,
          tableData,
        })
        return
      }

      await createTable({
        documentPageId: documentPage.id as string,
        tableData: {
          type: dataType,
          columns,
          rows,
        },
      })
    },
    [documentPage.id, dataType, template?.id, updateTable, createTable],
  )

  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )

  const selectTable = useCallback(() => {
    setSelectedElement({
      documentPageId: documentPage.id as string,
      id: draggableId,
      type: dataType,
    })
  }, [documentPage.id, draggableId, setSelectedElement])

  useEffect(() => {
    if (!tableData.length) {
      setTableData(() => [...initialTableData])
    }
  }, [tableData])

  useEffect(() => {
    if (toolsColumns) {
      setColumnsData((prev) => ({
        ...prev,
        ...toolsColumns.reduce(
          (acc, column) => ({
            ...acc,
            ...column,
          }),
          {},
        ),
      }))

      toolsColumns.forEach((column) => {
        Object.keys(column).forEach((accessorKey) => {
          const columnDef =
            initialColumns[accessorKey] ??
            (createNewColumn({
              accessorKey,
              onCellChange,
              selectTable,
              isReadOnly,
              documentPageId: documentPage.id as string,
              tableType: 'tools-table',
            }) as ColumnDef<ToolsTableData>)
          setColumnsDef((prev) => [...prev, columnDef])
        })
      })
    }

    return () => {
      setColumnsDef([])
    }
  }, [toolsColumns])

  useEffect(() => {
    if (Array.isArray(toolsRows)) {
      setTableData(() => [...(toolsRows as Array<ToolsTableData>)])
    }

    return () => {
      setTableData([])
    }
  }, [toolsRows])

  const onCellChange = useCallback(
    (rowIndex, columnId, value) => {
      setTableData((prev) =>
        prev.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row,
        ),
      )
    },
    [setTableData],
  )

  const onHeaderChange = useCallback((value: string, accessorKey: string) => {
    setColumnsData((prev) => ({
      ...prev,
      [accessorKey]: value,
    }))
  }, [])

  const saveToolsTableData = useCallback(async () => {
    const cleanColumns = columnsDef.reduce((accum, colDef: any) => {
      const columnAccessor = Object.keys(columnsData).find(
        (accessorKey) => accessorKey === colDef.accessorKey,
      )

      if (!columnAccessor) return accum

      const column = columnsData[columnAccessor]

      return [
        ...accum,
        {
          [columnAccessor]: column,
        },
      ]
    }, [] as Array<any>)
    await saveTableData({
      columns: cleanColumns,
      rows: tableData,
    })
  }, [tableData, columnsData, columnsDef, saveTableData])

  const addNewColumn = useCallback(
    async ({
      accessorKey,
      newColumn,
      position,
    }: {
      accessorKey: string
      newColumn: ColumnDef<any>
      position: 'left' | 'right'
    }) => {
      const newColumnAccessor = (newColumn as { accessorKey: string })
        .accessorKey
      const targetIndex = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )
      const insertIndex = position === 'left' ? targetIndex : targetIndex + 1

      const updatedColumns = [
        ...columnsDef.slice(0, insertIndex),
        { accessorKey: newColumnAccessor },
        ...columnsDef.slice(insertIndex),
      ].map((columnDef: any) => {
        const key = columnDef.accessorKey
        return { [key]: columnsData[key] ?? '' }
      })

      const updatedRows = [
        ...tableData.map((row) => ({
          ...row,
          [newColumnAccessor]: '',
        })),
      ]

      await saveTableData({
        columns: updatedColumns,
        rows: updatedRows,
      })
    },
    [columnsDef, tableData, saveTableData],
  )

  const removeColumn = useCallback(
    async (accessorKey: string) => {
      if (accessorKey && columnsDef.length === 1) {
        void deleteTable({
          templateAttributeId: template?.id as string,
          documentPageId: documentPage.id as string,
        })

        return
      }

      const columnBefore = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )

      if (columnBefore === -1) return
      setColumnsDef((prevColumns) => [
        ...prevColumns.slice(0, columnBefore),
        ...prevColumns.slice(columnBefore + 1),
      ])
      setTableData((prevTableData) =>
        prevTableData.filter((row) => Object.keys(row).includes(accessorKey)),
      )
      setColumnsData((prev) => {
        const newColumns = { ...prev }
        delete newColumns[accessorKey]
        return newColumns
      })

      const filteredToolsRows = tableData
        .map((row) => {
          return Object.keys(row).reduce((acc, currAccessorKey) => {
            if ((currAccessorKey as string) !== accessorKey) {
              acc[currAccessorKey] = row[currAccessorKey]
            }

            return acc
          }, {})
        })
        .filter((row) => Object.keys(row).length)

      const filteredColumns = columnsDef
        .filter(
          (column) => ((column as any).accessorKey as string) !== accessorKey,
        )
        .map((column) => {
          const accessorKey = (column as any).accessorKey
          return { [accessorKey]: columnsData[accessorKey] }
        })

      await saveTableData({
        columns: filteredColumns,
        rows: filteredToolsRows,
      })
    },
    [
      columnsDef,
      tableData,
      saveTableData,
      deleteTable,
      template?.id,
      documentPage.id,
      columnsData,
    ],
  )

  const removeRow = useCallback(
    async (rowIndex) => {
      const filteredRows = tableData.filter((_, index) => index !== rowIndex)
      setTableData((prev) => prev.filter((_, index) => index !== rowIndex))

      await saveTableData({
        rows: filteredRows,
      })
    },
    [tableData, setTableData, saveTableData],
  )

  const addNewRow = useCallback(
    async ({
      rowIndex,
      newRow,
      position,
    }: {
      rowIndex?: number
      newRow: any
      position: 'above' | 'below'
    }) => {
      let insertIndex: number

      if (typeof rowIndex === 'number') {
        insertIndex = position === 'above' ? rowIndex : rowIndex + 1
      } else {
        insertIndex = position === 'above' ? 0 : tableData.length
      }

      const updatedRows = [
        ...tableData.slice(0, insertIndex),
        newRow,
        ...tableData.slice(insertIndex),
      ]
      setTableData(updatedRows)

      await saveTableData({
        rows: updatedRows,
      })
    },
    [tableData],
  )

  useEffect(() => {
    if (columnsSize) {
      setColumnsDef((prevColumns) =>
        prevColumns.map((column) => {
          const accessor = (column as any).accessorKey
          const size = (columnsSize as any)[accessor]
          if (size) {
            return {
              ...column,
              size,
            }
          }
          return column
        }),
      )
    }
  }, [columnsSize])

  const isToolsTableEmpty = tableData.every((row) => !row.toolName)

  if (isReadOnly && isToolsTableEmpty) {
    return null
  }

  return (
    <EditableTable
      suggestions={{
        toolName: toolsBom?.map((tool) => tool.toolName) || [],
      }}
      saveTableData={saveToolsTableData}
      onCellChange={onCellChange}
      removeRow={removeRow}
      addRow={addNewRow}
      onHeaderChange={onHeaderChange}
      saveCustomColumns={saveToolsTableData}
      removeColumn={removeColumn}
      addColumn={addNewColumn}
      setSelectedColumn={setSelectedColumn}
      setSelectedRow={setSelectedRow}
      selectedRow={selectedRow}
      selectedColumn={selectedColumn}
      documentPage={documentPage}
      tableData={tableData}
      defaultRows={0}
      columnsDef={columnsDef as any}
      columnsData={columnsData}
      positionData={tablePosition}
      columnsSize={columnsSize || {}}
      tableTemplate={template as any}
      isReadOnly={isReadOnly}
      id="tools-table"
      type="tools-table"
      direction={isManual ? 'left' : 'right'}
      testId="tools-table"
      onNudgeEnd={onNudgeEnd}
      onNudgeStart={onNudgeStart}
    />
  )
}
